// src/sections/Dashboard.jsx — full replacement
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { fetchRunsStats, sumLastNDays } from '../services/api/statsApi.js';
import { listRuns } from '../admin/runsApi.js';
import ChartCard from '../components/charts/ChartCard.jsx';
import BarTopFailureReasons from '../components/charts/BarTopFailureReasons.jsx';
import RunsPerDayLine    from '../components/charts/RunsPerDayLine.jsx';
import PlatformTotalsBar from '../components/charts/PlatformTotalsBar.jsx';
import ContentFormatBar  from '../components/charts/ContentFormatBar.jsx';
import PlatformByFormatBar from '../components/charts/PlatformByFormatBar.jsx';
import MockShareChip     from '../components/charts/MockShareChip.jsx';
import CriticScoreHistogram from '../components/charts/CriticScoreHistogram.jsx';
import IterationCountBar   from '../components/charts/IterationCountBar.jsx';
import WarningsHistogram   from '../components/charts/WarningsHistogram.jsx';
import DurationHistogram   from '../components/charts/DurationHistogram.jsx';
import ActivityHeatmap     from '../components/charts/ActivityHeatmap.jsx';
import DateRangePicker     from '../admin/components/DateRangePicker.jsx';

const FONT = "'DM Sans', sans-serif";
const COLOR = { text: '#1d1d1f', muted: '#6c6c6d', okFg: '#10B981', errFg: '#EF4444', border: 'rgba(0,0,0,0.08)' };

function formatItalianDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Local-time YYYY-MM-DD (matches <input type="date"> and DateRangePicker output).
function toYmd(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// Default temporal filter: from one month ago to today (inclusive).
function defaultRange() {
  const now = new Date();
  const sinceDate = new Date(now);
  sinceDate.setMonth(sinceDate.getMonth() - 1);
  return { since: toYmd(sinceDate), before: toYmd(now) };
}

function KpiCard({ label, value, hint, accent }) {
  return (
    <div style={{
      flex: '1 1 0', minWidth: 160,
      padding: '16px 18px', borderRadius: 8,
      border: `1px solid ${COLOR.border}`, background: '#fff',
      fontFamily: FONT,
    }}>
      <div style={{ fontSize: 11, color: COLOR.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: accent || COLOR.text, marginTop: 6 }}>{value}</div>
      {hint && <div style={{ fontSize: 11, color: COLOR.muted, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function SectionHeader({ title, hint }) {
  return (
    <div style={{ margin: '28px 0 12px' }}>
      <h2 style={{ fontSize: 15, fontWeight: 700, color: COLOR.text, margin: 0 }}>{title}</h2>
      {hint && <div style={{ fontSize: 12, color: COLOR.muted, marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

function bucketCount(buckets, key) {
  if (!Array.isArray(buckets)) return 0;
  const b = buckets.find(x => x?.key === key);
  return b ? b.count : 0;
}

/**
 * Tenant-scoped dashboard body (KPIs + charts) for an explicit `tenantId`.
 * Extracted from the member Dashboard so the superadmin overview can reuse it
 * to render ANY tenant's numbers ("vedi come tenant").
 *
 * - `tenantId`     drives every fetch; when falsy nothing is loaded and an
 *                  empty prompt is shown (SA hasn't picked a tenant yet).
 * - `isSuperadmin` gates the ops-only charts (error codes / warnings) — passed
 *                  as a prop, NOT read from useAuth, so the SA overview shows
 *                  them regardless of which tenant is selected.
 * - `headerSlot`   page header (title + greeting, or the SA tenant selector),
 *                  rendered by the caller and placed above the content.
 */
export function DashboardView({ tenantId, isSuperadmin = false, headerSlot = null }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusData, setStatusData] = useState(null);    // RunStatsResponse | null
  const [dayData, setDayData] = useState(null);
  const [platformData, setPlatformData] = useState(null);
  const [contentFormatData, setContentFormatData] = useState(null);
  const [statusWithMockData, setStatusWithMockData] = useState(null);
  const [failedSample, setFailedSample] = useState(null);
  const [completedSample, setCompletedSample] = useState(null);
  const [allSample, setAllSample] = useState(null);
  const [range, setRange] = useState(() => defaultRange());
  const { since: sinceFilter, before: beforeFilter } = range;

  useEffect(() => {
    // No tenant selected → don't fire a tenant-less request; the empty prompt
    // below covers the "SA hasn't picked yet" case.
    if (!tenantId) return undefined;

    let cancelled = false;
    setLoading(true);
    setError('');

    const dateOpts = { since: sinceFilter, before: beforeFilter };

    // Threading tenantId into every call keeps the URL tenant-keyed, which
    // matters because the BE's Cache-Control: private, max-age=60 caches by
    // URL only (X-Tenant-Id is NOT a cache key). Without this, a tenant
    // switch within 60s would surface the previous tenant's numbers.
    Promise.allSettled([
      fetchRunsStats({ groupBy: 'status',         tenant: tenantId, ...dateOpts }),
      fetchRunsStats({ groupBy: 'day',            tenant: tenantId, ...dateOpts }),
      fetchRunsStats({ groupBy: 'platform',       tenant: tenantId, ...dateOpts }),
      fetchRunsStats({ groupBy: 'content_format', tenant: tenantId, ...dateOpts }),
      fetchRunsStats({ groupBy: 'status', includeMock: true, tenant: tenantId, ...dateOpts }),
      listRuns({ tenant: tenantId, status: ['failed'],    limit: 200, ...dateOpts }),
      listRuns({ tenant: tenantId, status: ['completed'], limit: 200, ...dateOpts }),
      listRuns({ tenant: tenantId, limit: 200, ...dateOpts }),
    ]).then((results) => {
      if (cancelled) return;
      const [s, d, p, cf, sMock, failed, completed, all] = results;
      setStatusData(s.status     === 'fulfilled' ? s.value     : null);
      setDayData(d.status        === 'fulfilled' ? d.value     : null);
      setPlatformData(p.status   === 'fulfilled' ? p.value     : null);
      setContentFormatData(cf.status === 'fulfilled' ? cf.value : null);
      setStatusWithMockData(sMock.status === 'fulfilled' ? sMock.value : null);
      setFailedSample(failed.status === 'fulfilled' ? (failed.value?.runs || []) : null);
      setCompletedSample(completed.status === 'fulfilled' ? (completed.value?.runs || []) : null);
      setAllSample(all.status === 'fulfilled' ? (all.value?.runs || []) : null);

      const rejected = results.filter(r => r.status === 'rejected');
      if (rejected.length === results.length) {
        setError(rejected[0].reason?.message || 'Errore nel caricamento delle statistiche');
      } else if (rejected.length > 0) {
        setError('Alcune statistiche non sono disponibili');
      }
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [tenantId, sinceFilter, beforeFilter]);

  const total      = statusData ? statusData.total : null;
  const completed  = statusData ? bucketCount(statusData.buckets, 'completed') : null;
  const failed     = statusData ? bucketCount(statusData.buckets, 'failed')    : null;
  const last7      = dayData    ? sumLastNDays(dayData.buckets, 7)             : null;
  const since      = statusData?.since || dayData?.since || platformData?.since;
  const before     = statusData?.before || dayData?.before || platformData?.before;

  const successRate = (() => {
    if (!statusData?.total) return null;
    const ok = bucketCount(statusData.buckets, 'completed')
             + bucketCount(statusData.buckets, 'approved');
    return Math.round((ok / statusData.total) * 100);
  })();

  const fmt = (n) => (n == null ? '—' : String(n));

  return (
    <div style={{ padding: '32px 40px', fontFamily: FONT, color: COLOR.text, maxWidth: '960px' }}>
      {headerSlot}

      {!tenantId ? (
        <div style={{
          padding: '60px 40px', textAlign: 'center', background: '#ffffff',
          borderRadius: '10px', border: '1px solid rgba(0,0,0,0.06)',
          color: COLOR.muted, fontSize: '13px',
        }}>
          Seleziona un tenant per visualizzare la dashboard.
        </div>
      ) : (
        <>
          {error && (
            <div role="alert" style={{
              marginBottom: 16, padding: '8px 12px', borderRadius: 6,
              border: `1px solid ${COLOR.errFg}`, color: COLOR.errFg,
              background: 'rgba(239,68,68,0.04)', fontSize: 12,
            }}>{error}</div>
          )}

          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            flexWrap: 'wrap', marginBottom: 20,
          }}>
            <span style={{ fontSize: 12, color: COLOR.muted }}>Periodo:</span>
            <DateRangePicker
              since={range.since}
              before={range.before}
              onChange={(patch) => setRange(r => ({ ...r, ...patch }))}
            />
            <button
              type="button"
              onClick={() => setRange(defaultRange())}
              style={{
                padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
                border: `1px solid ${COLOR.border}`, background: '#fff',
                fontSize: 12, color: COLOR.muted, fontFamily: FONT,
              }}
            >
              Ultimo mese
            </button>
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
            <KpiCard label="Run totali"      value={loading ? '…' : fmt(total)} />
            <KpiCard label="Completate"       value={loading ? '…' : fmt(completed)} accent={COLOR.okFg} />
            <KpiCard label="Fallite"          value={loading ? '…' : fmt(failed)}    accent={COLOR.errFg} />
            <KpiCard label="Ultimi 7 giorni"  value={loading ? '…' : fmt(last7)} />
            <KpiCard
              label="Tasso di successo"
              value={loading ? '…' : (successRate == null ? '—' : `${successRate}%`)}
              accent={COLOR.okFg}
            />
          </div>

          <MockShareChip realOnly={statusData} withMock={statusWithMockData} />

          {/* ── SECTION 1: Utilizzo & adozione ── */}
          <SectionHeader
            title="Utilizzo & adozione"
            hint="Quanto, dove e quando il team usa lo strumento"
          />

          <ChartCard
            title="Volume giornaliero delle run"
            subtitle="Numero di run create per giorno"
            loading={loading}
            empty={!loading && (!dayData || (dayData.total || 0) === 0)}
            reliability={{ kind: 'exact' }}
          >
            <RunsPerDayLine data={dayData} />
          </ChartCard>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 280px', minWidth: 280 }}>
              <ChartCard
                title="Volume per piattaforma"
                subtitle="Numero di run per piattaforma"
                loading={loading}
                empty={!loading && (!platformData || (platformData.total || 0) === 0)}
                reliability={{ kind: 'exact' }}
              >
                <PlatformTotalsBar data={platformData} />
              </ChartCard>
            </div>
            <div style={{ flex: '1 1 280px', minWidth: 280 }}>
              <ChartCard
                title="Volume per formato di contenuto"
                subtitle="Numero di run per formato"
                loading={loading}
                empty={!loading && (!contentFormatData || (contentFormatData.total || 0) === 0)}
                reliability={{ kind: 'exact' }}
              >
                <ContentFormatBar data={contentFormatData} />
              </ChartCard>
            </div>
          </div>

          <ChartCard
            title="Piattaforma per formato di contenuto"
            subtitle="Per ogni piattaforma, con quale formato di contenuto vengono create le run"
            loading={loading}
            empty={!loading && (!allSample || allSample.length === 0)}
            reliability={{ kind: 'sample', n: (allSample || []).length }}
          >
            <PlatformByFormatBar runs={allSample || []} />
          </ChartCard>

          <ChartCard
            title="Attività per giorno/ora"
            subtitle="Distribuzione delle run per giorno della settimana e ora locale"
            loading={loading}
            empty={!loading && (!allSample || allSample.length === 0)}
            reliability={{ kind: 'sample', n: (allSample || []).length }}
          >
            <ActivityHeatmap runs={allSample || []} />
          </ChartCard>

          {/* ── SECTION 2: Salute della pipeline ──
              Distribuzione per stato e imbuto di completamento nascosti finché il
              flow di accettazione delle run non è definito. Codici d'errore e
              warning per run sono visibili solo ai superadmin. */}
          {isSuperadmin && (
            <>
              <SectionHeader
                title="Salute della pipeline"
                hint="Se la generazione funziona e perché eventualmente fallisce"
              />

              <ChartCard
                title="Codici d'errore più frequenti"
                subtitle="Top 5 codici d'errore tra le run fallite più recenti, più 'Altri'"
                loading={loading}
                empty={!loading && (!failedSample || failedSample.length === 0)}
                reliability={{ kind: 'sample', n: (failedSample || []).length }}
              >
                <BarTopFailureReasons runs={failedSample || []} />
              </ChartCard>

              <ChartCard
                title="Warning per run"
                subtitle="Warning emessi durante la pipeline"
                loading={loading}
                empty={!loading && (!completedSample || completedSample.length === 0)}
                reliability={{ kind: 'sample', n: (completedSample || []).length }}
              >
                <WarningsHistogram runs={completedSample || []} />
              </ChartCard>
            </>
          )}

          {/* ── SECTION 3: Qualità & efficienza ── */}
          <SectionHeader
            title="Qualità & efficienza"
            hint="Quanto è buono e quanto è veloce l'output generato"
          />

          <ChartCard
            title="Distribuzione critic score"
            subtitle="Bin da 10 punti; punteggi nulli scartati"
            loading={loading}
            empty={!loading && (!completedSample || completedSample.length === 0)}
            reliability={{ kind: 'sample', n: (completedSample || []).length }}
          >
            <CriticScoreHistogram runs={completedSample || []} />
          </ChartCard>

          <ChartCard
            title="Iterazioni per run"
            subtitle="Numero di iterazioni del modello"
            loading={loading}
            empty={!loading && (!completedSample || completedSample.length === 0)}
            reliability={{ kind: 'sample', n: (completedSample || []).length }}
          >
            <IterationCountBar runs={completedSample || []} />
          </ChartCard>

          <ChartCard
            title="Durata delle run"
            subtitle="Tempo tra creazione e completamento"
            loading={loading}
            empty={!loading && (!completedSample || completedSample.length === 0)}
            reliability={{ kind: 'sample', n: (completedSample || []).length }}
          >
            <DurationHistogram runs={completedSample || []} />
          </ChartCard>

          {(since || before) && (
            <div style={{ fontSize: 11, color: COLOR.muted }}>
              Periodo: {formatItalianDate(since)} → {formatItalianDate(before)}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user, activeTenant, isSuperadmin, adminMode } = useAuth();

  // SA in adminMode operates entirely under /admin/* — bounce before fetching.
  if (isSuperadmin && adminMode) return <Navigate to="/admin/users" replace />;

  const header = (
    <>
      <h1 style={{ fontSize: '22px', fontWeight: 700, marginBottom: 4 }}>Dashboard</h1>
      <p style={{ color: COLOR.muted, fontSize: '13px', marginBottom: 24 }}>
        Benvenuto{user?.email ? `, ${user.email}` : ''}
        {activeTenant ? ` — ${activeTenant.display_name}` : ''}
      </p>
    </>
  );

  return (
    <DashboardView
      tenantId={activeTenant?.tenant_id}
      isSuperadmin={isSuperadmin}
      headerSlot={header}
    />
  );
}
